/** Reports' own date range; the top bar's week/month is greyed while Reports is open. */
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { T } from "./tokens";
import { addDays, mondayOf, toISODate } from "./data";

export interface ReportRange {
    from: string;
    to: string;
    preset: string;
}

const PRESETS: Array<{ key: string; label: string; range: () => [Date, Date] }> = [
    { key: "tw", label: "This week", range: () => { const m = mondayOf(new Date()); return [m, addDays(m, 4)]; } },
    { key: "tm", label: "This month", range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth(), 1), new Date(t.getFullYear(), t.getMonth() + 1, 0)]; } },
    { key: "lm", label: "Last month", range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]; } },
    { key: "3m", label: "Last 3 months", range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth() - 3, t.getDate()), addDays(t, -1)]; } },
    { key: "6m", label: "Last 6 months", range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth() - 6, t.getDate()), addDays(t, -1)]; } },
    { key: "12m", label: "Last 12 months", range: () => { const t = new Date(); return [new Date(t.getFullYear() - 1, t.getMonth(), t.getDate()), addDays(t, -1)]; } },
    { key: "ytd", label: "This year to date", range: () => { const t = new Date(); return [new Date(t.getFullYear(), 0, 1), addDays(t, -1)]; } },
];

const fromPreset = (key: string): ReportRange => {
    const p = PRESETS.find((x) => x.key === key) ?? PRESETS[2];
    const [a, b] = p.range();
    return { from: toISODate(a), to: toISODate(b), preset: p.key };
};

export const useReportRange = (initial = "lm") => useState<ReportRange>(() => fromPreset(initial));

/** Weekdays in the range, as ISO dates. */
export const weekdaysBetween = (from: string, to: string) => {
    const out: string[] = [];
    if (!from || !to || from > to) return out;
    for (let d = new Date(from); toISODate(d) <= to; d = addDays(d, 1)) {
        const w = d.getDay();
        if (w !== 0 && w !== 6) out.push(toISODate(d));
    }
    return out;
};

export const chipSx = (active: boolean) => ({
    border: `1px solid ${active ? T.purple : T.border}`,
    borderRadius: "999px",
    padding: "4px 11px",
    fontSize: "11.5px",
    fontWeight: 800,
    fontFamily: T.font,
    cursor: "pointer",
    background: active ? T.purpleTint : T.card,
    color: active ? T.purpleDeep : T.muted,
});

export const ReportRangeBar = ({ range, onChange }: { range: ReportRange; onChange: (r: ReportRange) => void }) => {
    const input = { fontFamily: T.font, fontSize: "12px", padding: "4px 8px", border: `1px solid ${T.border}`, borderRadius: "6px", color: T.ink2 };
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
            {PRESETS.map((p) => (
                <Box key={p.key} component="button" onClick={() => onChange(fromPreset(p.key))} sx={chipSx(range.preset === p.key)}>
                    {p.label}
                </Box>
            ))}
            <Box sx={{ display: "flex", gap: "6px", alignItems: "center", ml: "6px" }}>
                <Box component="input" type="date" value={range.from} max={range.to} onChange={(e: any) => onChange({ ...range, preset: "", from: e.target.value })} sx={input} />
                <Typography sx={{ fontSize: "12px", color: T.muted }}>to</Typography>
                <Box component="input" type="date" value={range.to} min={range.from} onChange={(e: any) => onChange({ ...range, preset: "", to: e.target.value })} sx={input} />
            </Box>
        </Box>
    );
};
